import * as React from 'react'
import { ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Switch, useHistory } from 'react-router-dom'
import { AppContext, IBackgroundLocationState } from '../config/appConfig'
import { useMount } from '@fluentui/react-hooks'
import { isAppContext } from '../helpers/appHelper'
import { Action, Location } from 'history'
import { Route } from 'react-router'
import Dashboard from './Dashboard'
import Files from './Files'
import Emails from './Emails'
import Parties from './Parties'
import Tasks, { taskBaseUrl } from './Tasks'
import Times, { timeBaseUrl } from './Times'
import MessageContext from '../context/MessageContext'
import FileContext from '../context/FileContext'
import { extractReferenceNumbers } from '../helpers/referenceNumberHelper'
import useAxios from '../hooks/useAxios'
import { ModuleType } from '../context/GlobalContext'
import useModules from '../hooks/module/useModules'
import { useMollie } from '../hooks/useMollie'
import { useSettings } from '../hooks/useSettings'
import InvoiceApprovals from './InvoiceApprovals'
import { Destination } from '../context/AxiosContext'
import Workflows, { workflowBaseUrl } from './Workflows'
import { isOfficeInitialized } from '../helpers/outlookHelper'
import Reports from './Finance/Reports'
import Invoices from './Invoices'
import CRMPages from './CRMPages'
import Loading from '../components/Loading'
import AuthorizationContext from '../context/AuthorizationContext'
import useLocalStorage from '../hooks/useLocalStorage'
import { COMMAND_PREFIX } from './Commands'

const getPathFromHash = (): string => window.location.hash.substring(1)
const hashIsCorrect = (location: Location): boolean => getPathFromHash() === location.pathname

export default function Router(): ReactElement {
    const { loaded: settingsLoaded, settings } = useSettings()
    const axios = useAxios()
    const { outlookMessage } = useContext(MessageContext)
    const { validateReferences } = useContext(FileContext)
    const history = useHistory<IBackgroundLocationState>()
    const { host: currentHost } = useContext(AuthorizationContext)
    const modules = useModules()
    const mollie = useMollie()

    const showDashboard = useMemo(() => settings?.dashboard?.enabled !== false, [settings])
    const [currentLocation, setCurrentLocation] = useState<{ location: Location; action: Action }>(undefined)

    const {
        loaded: currentPageLoaded,
        values: currentPage,
        setValues: setCurrentPage,
    } = useLocalStorage<string>({
        hostKey: currentHost?.RowKey, // Always defined: Router only mounts when authenticated.
        key: 'currentPage',
        defaultValue: null,
        fromJson: values => values,
        toJson: values => values,
    })

    // Track the current page per host, so a fresh task pane can restore it.
    useEffect(
        () =>
            history.listen((location: Location, action: Action) => {
                // Command dialogs are overlays on a background location, never a meaningful page to restore.
                if (['PUSH', 'REPLACE'].includes(action) && !location.pathname.startsWith(COMMAND_PREFIX)) {
                    setCurrentPage(location.pathname)
                }
            }),
        // The setter is a no-op until the hook has loaded and gets a new identity
        // afterwards — re-subscribe so navigations aren't dropped by a stale closure.
        [setCurrentPage],
    )

    // Restore once the per-host value has loaded.
    const restoredRef = useRef(false)
    useEffect(() => {
        if (!currentPageLoaded || restoredRef.current) {
            return
        }
        restoredRef.current = true
        // Never restore command dialog overlays (their location state is gone), and in compose mode
        // don't restore the global e-mail page: it files a received e-mail, which compose doesn't have (HAM-4227).
        const restorable = currentPage && !currentPage.startsWith(COMMAND_PREFIX) && !(isAppContext(AppContext.Compose) && currentPage === '/email')
        // We stick to the current page if it's not 'home' or restore the last visited page.
        if (window.location.hash?.length > 2) {
            history.push(window.location.hash.substring(1))
        } else if (restorable) {
            history.push(currentPage)
        }
    }, [currentPageLoaded])

    useMount(() => {
        mollie.authorize(window.location)
    })

    useEffect(() => {
        if (
            isOfficeInitialized() &&
            isAppContext(AppContext.Compose) &&
            outlookMessage &&
            (outlookMessage as Office.MessageCompose)?.subject?.getAsync
        ) {
            ;(outlookMessage as Office.MessageCompose)?.subject?.getAsync(subject =>
                validateReferences(extractReferenceNumbers(subject.value)).then(fileIdMap => {
                    if (fileIdMap.size === 1) {
                        const [fileId] = fileIdMap.values()
                        history.push(`/file/${fileId}`)
                    }
                }),
            )
        }
    }, [outlookMessage])

    useEffect(() => {
        const listener = () => {
            // Use the history object, because the useLocation() object is not yet updated when this method is called.
            // This causes issues with preserving the state when showing command dialogs.
            if (!hashIsCorrect(history.location)) {
                history.replace(getPathFromHash(), history.location.state)
            }
        }

        // Make sure that the path in memory gets updated if the hash changes by an external event.
        window.addEventListener('hashchange', listener)

        return () => window.removeEventListener('hashchange', listener)
    }, [])

    useEffect(
        () =>
            history.listen((location: Location, action: Action) => {
                setCurrentLocation({ location, action })
            }),
        [],
    )

    useEffect(() => {
        const isFilePath = currentLocation?.location.pathname.match(`.*/file/([^/?\\s]+)`)

        if (isFilePath && ['PUSH'].includes(currentLocation?.action) && currentHost?.RowKey) {
            if (modules.has(ModuleType.AuditTrailTracking)) {
                axios.post(
                    `/audit-trail/${currentHost?.RowKey}`,
                    { path: currentLocation.location.pathname },
                    {
                        baseURL: process.env.API_HOST,
                        headers: {
                            SuppressErrors: true,
                            Destination: Destination.Hammock,
                        },
                    },
                )
            } else if (!modules.has(ModuleType.AuditTrailTracking)) {
                axios.post('/file/navigate', currentLocation.location.pathname, {
                    headers: { 'Content-Type': 'text/plain', SuppressErrors: true },
                })
            }
        }
    }, [currentLocation])

    // Make sure that the hash location gets updated every time the page changes.
    window.location.hash = `#${history.location.pathname}`

    if (!settings) {
        return <></>
    }

    if (settings?.dashboard?.enabled === false && history?.location?.pathname === '/') {
        history.push('/file')
    }

    if (!settingsLoaded) {
        return <Loading />
    }

    return (
        <Switch location={history.location.state?.background || history.location}>
            {showDashboard && <Route exact path="/" component={Dashboard} />}
            {modules.has(ModuleType.CRM) && <Route path="/crm" component={CRMPages} />}
            <Route path="/file" component={Files} />
            <Route path="/email" component={Emails} />
            <Route path="/party" component={Parties} />
            {modules.has(ModuleType.Tasks) && <Route path={taskBaseUrl} component={Tasks} />}
            {modules.has(ModuleType.Workflow) && <Route path={workflowBaseUrl} component={Workflows} />}
            {modules.has(ModuleType.TimeTracking) && <Route path={timeBaseUrl} component={Times} />}
            {modules.has(ModuleType.InvoiceApproval) && <Route path="/invoice-approval" component={InvoiceApprovals} />}
            {modules.has(ModuleType.Invoices) && <Route path="/invoice" component={Invoices} />}
            {modules.has(ModuleType.Reports) && <Route path="/reports" component={Reports} />}
        </Switch>
    )
}
